import * as z from 'zod';
import { TOTAL_SYMBOLS } from '../pass/alphabet.js';
import { isWellFormedPassCode } from '../pass/codec.js';
import { idempotencyKeySchema, isoDateTimeSchema, reasonCodeSchema, recordActionMethodSchema, shortRefSchema, strict, uuidSchema, versionSchema, } from './common.js';
/**
 * The pass, as an entity (AD-29).
 *
 * A credential has a TYPE, and presence and audit never branch on it. `qr` is the only one enabled
 * in 1.0; `nfc_uid` and `nfc_written` are reserved and sit behind a disabled flag, which is the
 * whole of the NFC-readiness requirement. Making the type a first-class column is also what gives
 * reissue-and-revoke for free.
 */
export const credentialTypeSchema = z.enum(['qr', 'nfc_uid', 'nfc_written']);
/** The only type a 1.0 installation may issue. The others are reserved, not available. */
export const ENABLED_CREDENTIAL_TYPES = ['qr'];
export const credentialStatusSchema = z.enum(['active', 'revoked', 'superseded']);
/**
 * AD-59: how the code came into existence, recorded forever on the row.
 *
 * `minted` is the original on-demand path, `preprinted` is a claimed blank, and `emergency_mint`
 * is the break-glass mint an administrator takes in pre-printed mode when the box is empty. The
 * third is a column rather than an audit-only fact on purpose: the abuse it permits has to stay
 * visible after the shift that took it, and the dashboard counts it.
 */
export const credentialOriginSchema = z.enum(['minted', 'preprinted', 'emergency_mint']);
/**
 * AD-59: which of the two registration procedures this installation is running.
 *
 * It comes from `PASS_ISSUANCE_MODE` in the environment and is read once at boot, so changing it
 * needs a restart. That is deliberate and the reason is operational rather than technical: a
 * settings row read once per request would be perfectly atomic, but flipping which procedure the
 * desk is running must be a deliberate act rather than something that can change under a shift on
 * a shared terminal. Both modes leave already-issued credentials completely valid.
 */
export const passIssuanceModeSchema = z.enum(['preprinted', 'on_demand']);
/**
 * A pass code as it arrives from an operator or a scanner.
 *
 * The check symbol is verified here, in the schema, so a mistyped code never becomes a database
 * lookup. The upper bound is generous rather than exact because the input may carry hyphens and
 * spaces; the codec is what decides whether it is a code.
 */
export const passCodeSchema = z
    .string()
    .min(TOTAL_SYMBOLS, { error: 'errors.credential.code_malformed' })
    .max(64, { error: 'errors.credential.code_malformed' })
    .refine(isWellFormedPassCode, { error: 'errors.credential.code_malformed' });
/**
 * A code in its CANONICAL form: what the index is computed over and what the secret column holds.
 *
 * It is a different shape from {@link passCodeSchema} and the difference is not cosmetic. A printed
 * pass canonicalises to the bare code, because that is the string the batch importer hashed and any
 * decoration would orphan every band in the box. Every other type prefixes its own name, because
 * all three share one blind-index domain and a written tag carrying the same twenty-one symbols as
 * a printed band must not collide with it. So `issuedCredentialSchema` cannot reuse the inbound
 * schema: an `nfc_written` issue produces `nfc_written/<code>`, which `isWellFormedPassCode`
 * rejects, and the response the server itself built would fail its own contract.
 *
 * **What is checked here is the GRAMMAR, not the payload.** The prefix must be a declared type and
 * the remainder must be uppercase alphanumeric, which covers a pass code and a hex UID alike; what
 * counts as a well-formed payload for a type is the business of the resolver that produced it, and
 * restating either format here would be the second implementation this project refuses everywhere
 * else — two things to keep in step, failing as unusable hardware.
 */
export const canonicalPassCodeSchema = z.union([
    passCodeSchema,
    z
        .string()
        .max(80, { error: 'errors.credential.code_malformed' })
        .refine((value) => {
        const separator = value.indexOf('/');
        if (separator <= 0) {
            return false;
        }
        const prefix = value.slice(0, separator);
        const payload = value.slice(separator + 1);
        return (credentialTypeSchema.safeParse(prefix).success &&
            prefix !== 'qr' &&
            /^[0-9A-Z]+$/u.test(payload));
    }, { error: 'errors.credential.code_malformed' }),
]);
export const credentialSchema = strict({
    id: uuidSchema,
    personId: uuidSchema,
    type: credentialTypeSchema,
    status: credentialStatusSchema,
    issuedAt: isoDateTimeSchema,
    issuedBy: uuidSchema.nullable(),
    revokedAt: isoDateTimeSchema.nullable(),
    revokedBy: uuidSchema.nullable(),
    revokedReason: reasonCodeSchema.nullable(),
    expiresAt: isoDateTimeSchema.nullable(),
    version: versionSchema,
});
/**
 * Issuing or reissuing a pass.
 *
 * **The code is never CHOSEN by a caller**, which is a narrower and now more accurate statement
 * than "never accepted from one". In `on_demand` mode the server mints it from the approved
 * entropy source. In `preprinted` mode the operator does submit one, because it is printed on the
 * band in their hand — and what preserves the invariant is AD-60: the code must already exist as
 * an unclaimed blank of an imported batch, so a careless or compromised operator cannot assign a
 * guessable one, and a code nobody imported resolves to nothing.
 *
 * `passCode` is therefore optional rather than forbidden. This schema is strict, so without the
 * field a create-with-band would be refused as an unknown key rather than ignored.
 */
export const issueCredentialRequestSchema = strict({
    personId: uuidSchema,
    type: credentialTypeSchema.default('qr'),
    /** Supersede the person's current active pass of this type rather than refusing. */
    supersedeExisting: z.boolean().default(false),
    /** The code off a pre-printed band (AD-60). Required in `preprinted` mode, refused otherwise. */
    passCode: passCodeSchema.optional(),
    /**
     * AD-30, and this route needs it for the SUPERSEDE half specifically.
     *
     * A first issue is protected by the engine: `credentials_active_per_person_key` is a partial
     * unique index over `status = 'active'`, so a replayed issue is a typed refusal rather than a
     * second live pass. A REISSUE is not, and cannot be — it retires the existing row before it
     * inserts, precisely so that index cannot fire. Without a key, an operator's double-click on
     * "replace pass" supersedes the pass the first click had just minted: the code they have already
     * read aloud stops resolving at the gate, and there are two extra rows to explain.
     *
     * There is no `version` here to guard with instead, and adding one would be the wrong shape:
     * a reissue names a PERSON and a type, not the specific row it happens to be replacing.
     */
    idempotencyKey: idempotencyKeySchema,
});
export const revokeCredentialRequestSchema = strict({
    reasonCode: reasonCodeSchema,
    version: versionSchema,
});
/**
 * The one time a code is returned in full: immediately after minting, to be printed.
 *
 * Every later read returns the credential without its code. The stored ciphertext exists so the
 * pass can be reprinted, and that path is separately authorised and audited.
 *
 * **`printOffered` is answered here rather than derived by the caller**, and it is the same question
 * {@link revealedPassSchema} answers for a stored pass. It cannot be computed from anything else in
 * this shape: `origin` is deliberately absent from {@link credentialSchema}, so a client holding
 * only the installation's issuance mode would have to guess, and the guess is wrong for exactly one
 * credential — the emergency mint, which is the one pass in a pre-printed installation with no band
 * behind it and therefore the one that most needs its sheet. A browser that derived
 * `mode === 'on_demand'` here withheld the sheet from precisely that pass.
 */
export const issuedCredentialSchema = strict({
    credential: credentialSchema,
    // The CANONICAL form, which for anything but a printed pass carries its type as a prefix. The
    // inbound `passCodeSchema` would reject exactly those, so a route serialising an NFC issue
    // through this schema would refuse a response the server itself built.
    code: canonicalPassCodeSchema,
    display: z.string(),
    printOffered: z.boolean(),
});
/** Listing a person's passes. `personId` is required: there is no "every pass in the building". */
export const credentialListQuerySchema = strict({ personId: uuidSchema });
/**
 * The desk check for a scanned band (Section 1.6, AD-28).
 *
 * **Body-only, and that is the whole reason this is a POST.** A code in a path or a query parameter
 * is written in cleartext into two access logs, browser history and any `Referer`, all outside the
 * encryption boundary and rotated rather than shredded — and a code plus a scan is full
 * impersonation at the gate.
 */
export const bandLookupRequestSchema = strict({
    code: passCodeSchema,
    type: credentialTypeSchema.default('qr'),
});
/**
 * What the desk is told about a scanned band.
 *
 * **`available` is the SUCCESS here**, which is what makes this a different operation from resolving
 * a scan at the gate rather than the same one with a flag: resolve answers "may this person come
 * in", so an unclaimed band is a problem, and this answers "is this band usable for the person
 * standing here", so an unclaimed band is the answer the operator wanted.
 *
 * **A claimed band discloses a `short_ref` and nothing else.** That reference is the thing an
 * operator quotes and can then look up under their own permissions; a name or a photograph here
 * would turn a code-probing surface into a disclosure surface, since the caller supplies the code
 * and would learn who holds it.
 */
export const bandLookupSchema = z.discriminatedUnion('state', [
    strict({
        state: z.literal('available'),
        batchId: uuidSchema,
        serial: z.int().positive(),
        type: credentialTypeSchema,
    }),
    strict({ state: z.literal('claimed'), shortRef: shortRefSchema }),
    strict({ state: z.literal('void') }),
    strict({ state: z.literal('unknown') }),
]);
/** Showing a stored code again, and recording that it was shown. */
export const revealPassRequestSchema = strict({
    /** AD-53: how the action was reached, which is what makes the manual-versus-scan ratio possible. */
    method: recordActionMethodSchema.optional(),
});
/**
 * A code shown once, with everything the screen needs beside it.
 *
 * `printOffered` is a property of the installation and of this credential's `origin` rather than of
 * the caller: a pre-printed band already exists as a physical token, and a second one on A4 would be
 * two tokens for one credential (AD-59). {@link issuedCredentialSchema} carries the same flag for
 * the same reason, so the two delivery paths a pass can reach a screen by — just issued, and shown
 * again later — answer this question from one authority rather than from two.
 */
export const revealedPassSchema = strict({
    credential: credentialSchema,
    code: canonicalPassCodeSchema,
    display: z.string(),
    shortRef: shortRefSchema,
    printOffered: z.boolean(),
});
/**
 * Rendering the A4 sheet.
 *
 * The first name travels in the REQUEST rather than being read by the credential domain, because it
 * lives in the person's encrypted field values and that domain has no business reaching across into
 * them: the layer that authorised the print has already read the record it is printing for.
 */
export const printPassRequestSchema = strict({
    firstName: z.string().trim().min(1, { error: 'errors.validation.required' }).max(200),
    method: recordActionMethodSchema.optional(),
});
/** The rendered sheet, plus the pass it was rendered from. */
export const printedPassSchema = strict({ html: z.string(), pass: revealedPassSchema });
//# sourceMappingURL=credential.js.map